import { useMemo, useState, useEffect } from 'react';

import { roles } from 'src/utils/roles';
import { logger } from 'src/utils/logger';

import { ICONS } from '../utils/icons';
import { paths } from '../routes/paths';
import { useJsrClient } from './use-jsr-client';

/**
 * Hook template to manage layout navigation data
 * Handles transformations and integrations for dashboard navigation
 *
 * @param {Array} baseNavData - Base navigation configuration
 * @param {boolean} canUseGlobalInsights - Whether the user has ROLE_AI_GLOBAL
 * @returns {Array} Processed navigation data
 *
 * @example
 * const navData = useLayoutNav(dashboardNavData, canUseGlobalInsights);
 */
export function useLayoutNav(baseNavData, canUseGlobalInsights) {
    const { isJsrAvailable, getFavorites, getDashboards } = useJsrClient();
    const [dashboards, setDashboards] = useState(null);
    const [favorites, setFavorites] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch resources from JasperReports Server
    useEffect(() => {
        const fetchFavorites = async () => {
            try {
                setIsLoading(true);
                const data = await getFavorites(null, ['reportUnit']);
                setFavorites(data);
            } catch (error) {
                logger.error('Failed to fetch resources:', error);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchDashboards = async () => {
            try {
                setIsLoading(true);
                const data = await getDashboards();
                setDashboards(data);
            } catch (error) {
                logger.error('Failed to fetch dashboards:', error);
            } finally {
                setIsLoading(false);
            }
        }

        if (isJsrAvailable) {
            fetchFavorites();
            fetchDashboards();
        }
    }, [isJsrAvailable]);


    return useMemo(() => {
        // Start with base navigation
        let processedNav = baseNavData;

        // Users with ROLE_AI but not ROLE_AI_GLOBAL have no "new global chat" access,
        // so the Insights nav item should take them straight to their chat history.
        if (!canUseGlobalInsights) {
            processedNav = processedNav.map(section => ({
                ...section,
                items: section.items.map(item =>
                    item.feature === 'chat' ? { ...item, path: `${paths.chat}?view=chats` } : item
                ),
            }));
        }

        // ~ ~ ~ ~ ~ ~ ~ ~ ~ JSR Integration ~ ~ ~ ~ ~ ~ ~ ~ ~
        if (!!favorites && !!dashboards && !isLoading) {
            const reportNav = { ...processedNav.find(nav => nav.subheader === 'Reporting') };

            const map = res => ({
                title: res.label,
                path: `${paths[res.resourceType == 'reportUnit' ? 'reports' : 'dashboards'].view(res.uri)}?type=${res.resourceType}`,
            })

            const noItem = {
                title: 'No items found',
                path: '#',
            }

            const _favorites = favorites.filter(d => d.resourceType === 'reportUnit').map(map)
            const _dashboards = dashboards.filter(d => d.resourceType === 'dashboard').map(map)

            reportNav.items = [
                {
                    title: 'Favorites',
                    path: paths.reports.list,
                    icon: ICONS.favorites,
                    children: _favorites.length > 0 ? _favorites : [noItem],
                    roles: [roles.VIEW_REPORTS]
                },
                {
                    title: 'Dashboards',
                    path: paths.dashboards.list,
                    icon: ICONS.dashboard,
                    children: _dashboards.length > 0 ? _dashboards : [noItem],
                    roles: [roles.VIEW_REPORTS]
                },
                ...reportNav.items
            ];

            processedNav = [
                ...processedNav.slice(0, 1),
                reportNav,
                ...processedNav.slice(2)
            ];
        }
        // ~ ~ ~ ~ ~ ~ ~ ~ ~ JSR Integration ~ ~ ~ ~ ~ ~ ~ ~ ~

        return processedNav;
    }, [
        favorites,
        dashboards,
        isLoading,
        baseNavData,
        canUseGlobalInsights,
    ]);
}
